/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V102 - Strict Lock Check) ---
 * - MISSION: To fix the bug where the wallet appeared connected even
 * when MetaMask was locked.
 *
 * - THE BUG: The previous logic allowed `eth_accounts` to return an
 * address (cached permissions) even if the wallet was locked, causing
 * the UI to show a "connected" state.
 *
 * - THE FIX (Strict isUnlocked Check):
 * 1. The `checkConnection` function now prioritizes `_metamask.isUnlocked()`.
 * 2. If `isUnlocked()` returns `false`, we FORCE `setAccount(null)`,
 * regardless of what `eth_accounts` returns.
 * 3. This ensures the UI correctly reverts to "Connect Wallet" when the
 * extension is locked.
 */
import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';

// The static import for 'ethers' has been removed to enable dynamic loading.

const WalletContext = createContext(null);

/**
 * Custom hook to access the wallet context.
 */
// The linter rule is disabled here as it's a common and accepted pattern
// to export a custom hook alongside its provider from the same file.
// eslint-disable-next-line react-refresh/only-export-components
export const useWallet = () => {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error("useWallet must be used within a WalletProvider");
    }
    return context;
};

/**
 * SECURITY: Sanitizes error messages to prevent exposing internal library details.
 * @param {Error} error - The original error object.
 * @returns {string} A user-friendly error message.
 */
const sanitizeError = (error) => {
    if (error?.code === 4001) {
        return "Connection request rejected by user.";
    }
    if (error?.message?.includes("MetaMask is not installed")) {
        return "MetaMask is not installed. Please install the extension.";
    }
    console.error("Wallet Error:", error); // Log the full error for debugging
    return "An unexpected error occurred. Please try again.";
};


const PULSECHAIN_ID = '0x171'; // 369
const PULSECHAIN_CONFIG = {
    chainId: PULSECHAIN_ID,
    chainName: 'PulseChain',
    nativeCurrency: { name: 'Pulse', symbol: 'PLS', decimals: 18 },
    rpcUrls: ['https://rpc.pulsechain.com'],
    blockExplorerUrls: ['https://scan.pulsechain.com']
};

const switchNetwork = async (provider) => {
    try {
        await provider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: PULSECHAIN_ID }],
        });
    } catch (switchError) {
        // This error code indicates that the chain has not been added to MetaMask.
        if (switchError.code === 4902) {
            try {
                await provider.request({
                    method: 'wallet_addEthereumChain',
                    params: [PULSECHAIN_CONFIG],
                });
                // After adding, explicitly switch to the network
                await provider.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: PULSECHAIN_ID }],
                });
            } catch (addError) {
                console.error("Failed to add PulseChain:", addError);
                throw addError;
            }
        } else {
            console.error("Failed to switch network:", switchError);
            throw switchError;
        }
    }
};

// Helper to find the specific MetaMask provider or fallback
const getEthereumProvider = () => {
    if (typeof window.ethereum === 'undefined') return null;

    // Check for multiple providers (EIP-6963 / Legacy pattern)
    if (window.ethereum.providers) {
        const provider = window.ethereum.providers.find(p => p.isMetaMask);
        if (provider) return provider;
    }

    // Fallback to standard injection
    if (window.ethereum.isMetaMask) return window.ethereum;

    return window.ethereum; // Last resort fallback
};

export const WalletProvider = ({ children }) => {
    const [account, setAccount] = useState(null);
    const [provider, setProvider] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Lazy Connection: Auto-reconnect if previously connected (user can manually disconnect)
    const [shouldConnect, setShouldConnect] = useState(() => {
        return localStorage.getItem('walletConnected') === 'true';
    });

    /**
     * Disconnects the wallet by clearing all related state.
     * Wrapped in useCallback for performance optimization.
     */
    const disconnectWallet = useCallback(() => {
        setAccount(null);
        setProvider(null);
        setError(null);
        setShouldConnect(false);
        localStorage.removeItem('walletConnected');
    }, []);


    // Use a ref to track the current account to avoid stale closures in the interval
    const accountRef = useRef(account);

    useEffect(() => {
        accountRef.current = account;
    }, [account]);

    // --- ACTIVE CONNECTION CHECK ---
    useEffect(() => {
        // If the user hasn't connected before, do NOT poll or check connection.
        if (!shouldConnect) return;

        const checkConnection = async () => {
            const ethProvider = getEthereumProvider();
            if (!ethProvider) return;

            try {
                // If unlocked, check for exposed accounts
                const accounts = await ethProvider.request({ method: 'eth_accounts' });
                const newAccount = accounts?.[0]?.toLowerCase();

                if (newAccount && newAccount !== accountRef.current?.toLowerCase()) {
                    const { ethers } = await import('ethers');
                    const web3Provider = new ethers.BrowserProvider(ethProvider, 'any');
                    setProvider(web3Provider);
                    setAccount(newAccount);
                } else if (!newAccount) {
                    // No accounts exposed (treat as disconnected)
                    if (accountRef.current) {
                        setAccount(null);
                        setProvider(null);
                        setShouldConnect(false);
                        localStorage.removeItem('walletConnected');
                    }
                }
            } catch (err) {
                console.warn("Connection check failed:", err);
                // On error, assume disconnected for safety
                setAccount(null);
                setProvider(null);
            }
        };

        // Initial check with delay to ensure injection is complete
        const initialCheckTimer = setTimeout(checkConnection, 500);

        const ethProvider = getEthereumProvider();
        if (ethProvider) {
            const handleAccountsChanged = (accounts) => {
                if (accounts.length > 0) {
                    setAccount(accounts[0]);
                } else {
                    setAccount(null);
                    setProvider(null);
                    setShouldConnect(false);
                    localStorage.removeItem('walletConnected');
                }
            };
            const handleChainChanged = () => {
                checkConnection();
            };

            const handleVisibilityChange = () => {
                if (document.visibilityState === 'visible' && shouldConnect) {
                    checkConnection();
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);
            ethProvider.on('accountsChanged', handleAccountsChanged);
            ethProvider.on('chainChanged', handleChainChanged);

            return () => {
                clearTimeout(initialCheckTimer);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                ethProvider.removeListener('accountsChanged', handleAccountsChanged);
                ethProvider.removeListener('chainChanged', handleChainChanged);
            };
        }
    }, [shouldConnect]);

    const connectWallet = useCallback(async (walletType = 'metamask') => {
        setIsLoading(true);
        setError(null);
        try {
            const { ethers } = await import('ethers');
            if (walletType === 'metamask') {
                const ethProvider = getEthereumProvider();
                if (!ethProvider) throw new Error("MetaMask is not installed.");

                // ALWAYS request permissions to force user interaction
                // This prevents eth_requestAccounts from returning cached accounts when locked
                await ethProvider.request({
                    method: 'wallet_requestPermissions',
                    params: [{ eth_accounts: {} }]
                });

                const accounts = await ethProvider.request({ method: 'eth_requestAccounts' });

                // Enforce PulseChain
                await switchNetwork(ethProvider);

                const web3Provider = new ethers.BrowserProvider(ethProvider, 'any');
                setProvider(web3Provider);
                setAccount(accounts[0]);
                setShouldConnect(true);
                localStorage.setItem('walletConnected', 'true');
            }
        } catch (err) {
            // Ignore user rejection if they just closed the modal without selecting
            if (err.code === 4001) {
                console.log("User rejected connection request");
                return;
            }
            setError(sanitizeError(err));
        } finally {
            setIsLoading(false);
        }
    }, []);


    const value = {
        account,
        provider,
        connectWallet,
        disconnectWallet,
        error,
        isLoading,
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
};
